import {
  CallClient,
  CallAgent,
  Call,
  VideoStreamRenderer,
  LocalVideoStream,
  RemoteVideoStream,
  DeviceManager,
  RoomCallLocator,
} from "@azure/communication-calling";
import { AzureCommunicationTokenCredential } from "@azure/communication-common";

const activeStreams = new Set<MediaStream>();

if (typeof window !== "undefined" && window.navigator && window.navigator.mediaDevices && window.navigator.mediaDevices.getUserMedia) {
  try {
    const originalGetUserMedia = window.navigator.mediaDevices.getUserMedia.bind(window.navigator.mediaDevices);
    window.navigator.mediaDevices.getUserMedia = async function (constraints) {
      const stream = await originalGetUserMedia(constraints);
      if (stream) {
        activeStreams.add(stream);
      }
      return stream;
    };
  } catch (e) {
    console.warn("Failed to patch getUserMedia:", e);
  }
}

export type CallState =
  | "idle"
  | "initializing"
  | "connecting"
  | "connected"
  | "disconnected"
  | "ending";

export interface CallManagerState {
  callState: CallState;
  isMicOn: boolean;
  isCameraOn: boolean;
  isConnected: boolean;
  remoteParticipantName: string;
  hasRemoteVideo: boolean;
}

type Listener = (state: CallManagerState) => void;

class CallManagerClass {
  private callClient: CallClient | null = null;
  private callAgent: CallAgent | null = null;
  private call: Call | null = null;
  private deviceManager: DeviceManager | null = null;
  private localVideoStream: LocalVideoStream | null = null;
  private localRenderer: VideoStreamRenderer | null = null;
  private remoteRenderer: VideoStreamRenderer | null = null;

  // DOM attachment targets — set by the UI component
  private localVideoTarget: HTMLDivElement | null = null;
  private remoteVideoTarget: HTMLDivElement | null = null;

  // Subscribed participants to avoid double-subscription
  private subscribedParticipants = new Set<string>();

  // State
  private state: CallManagerState = {
    callState: "idle",
    isMicOn: true,
    isCameraOn: true,
    isConnected: false,
    remoteParticipantName: "",
    hasRemoteVideo: false,
  };

  private listeners = new Set<Listener>();
  private onCallEndedCallbacks = new Set<() => void>();
  private remoteLeftTimeout: NodeJS.Timeout | null = null;
  private stateChangedHandler: (() => void) | null = null;
  private remoteParticipantsUpdatedHandler: ((e: any) => void) | null = null;
  private isRenderingRemote = false;
  private eventCleanups: (() => void)[] = [];

  // ─── Public API ───────────────────────────────────────────

  getState(): CallManagerState {
    return { ...this.state };
  }

  isActive(): boolean {
    return (
      this.state.callState !== "idle" &&
      this.state.callState !== "disconnected"
    );
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  addOnCallEnded(cb: () => void) {
    this.onCallEndedCallbacks.add(cb);
  }

  removeOnCallEnded(cb: () => void) {
    this.onCallEndedCallbacks.delete(cb);
  }

  /**
   * Attach DOM nodes for video rendering.
   * Called on every mount of CustomCallView.
   * If call is already active, immediately re-renders video into new nodes.
   */
  async attachVideos(
    localTarget: HTMLDivElement,
    remoteTarget: HTMLDivElement
  ) {
    this.localVideoTarget = localTarget;
    this.remoteVideoTarget = remoteTarget;

    // Clear DOM nodes first
    localTarget.innerHTML = "";
    remoteTarget.innerHTML = "";

    // Re-render local video if stream exists
    if (this.localVideoStream) {
      if (this.localRenderer) {
        try { this.localRenderer.dispose(); } catch (_) {}
        this.localRenderer = null;
      }
      try {
        const renderer = new VideoStreamRenderer(this.localVideoStream);
        this.localRenderer = renderer;
        const view = await renderer.createView({ scalingMode: "Crop" });
        if (this.localVideoTarget) {
          this.localVideoTarget.innerHTML = "";
          this.localVideoTarget.appendChild(view.target);
        }
      } catch (_) {}
    }

    // Re-render remote video if stream exists
    if (this.call) {
      this.call.remoteParticipants.forEach((participant) => {
        participant.videoStreams.forEach((stream) => {
          if (stream.isAvailable) {
            this.renderRemoteStream(stream);
          }
        });
      });
    }
  }

  /**
   * Detach DOM nodes without stopping the call.
   * Called when navigating away (Notes, Rx, back button).
   */
  detachVideos() {
    // Clear DOM references — renderers keep running, tracks stay active
    if (this.localVideoTarget) this.localVideoTarget.innerHTML = "";
    if (this.remoteVideoTarget) this.remoteVideoTarget.innerHTML = "";
    this.localVideoTarget = null;
    this.remoteVideoTarget = null;
  }

  /**
   * Initialize ACS and join the room.
   * Only runs once — if call is already active, does nothing.
   */
  async joinRoom(params: {
    token: string;
    roomId: string;
    displayName: string;
  }) {
    if (this.isActive()) return; // already in a call

    this.setState({ callState: "initializing" });

    try {
      if (!this.callClient) {
        this.callClient = new CallClient();
      }
      const callClient = this.callClient;
      const credential = new AzureCommunicationTokenCredential(params.token);
      const agent = await callClient.createCallAgent(credential, {
        displayName: params.displayName,
      });
      this.callAgent = agent;

      // Get device manager and request permissions
      this.deviceManager = await callClient.getDeviceManager();
      const permissions = await this.deviceManager.askDevicePermission({ video: true, audio: true });
      if (!permissions.audio) {
        alert("Microphone permission denied. Other participants will not be able to hear you. Please grant permission in browser settings.");
      }
      if (!permissions.video) {
        alert("Camera permission denied. Joining call with audio only. Please grant permission in browser settings if you want to turn on video.");
      }
      const cameras = await this.deviceManager.getCameras();

      // Create local video stream
      if (cameras.length > 0) {
        this.localVideoStream = new LocalVideoStream(cameras[0]);
        if (this.localVideoTarget) {
          await this.renderLocalStream();
        }
      }

      this.setState({ callState: "connecting" });

      // Join the ACS room
      const locator: RoomCallLocator = { roomId: params.roomId };
      const call = agent.join(locator, {
        videoOptions: this.localVideoStream
          ? { localVideoStreams: [this.localVideoStream] }
          : undefined,
      });
      this.call = call;

      this.stateChangedHandler = () => {
        if (call.state === "Connected") {
          this.setState({ callState: "connected", isConnected: true });
        }
        if (call.state === "Disconnected") {
          const wasEnding = this.state.callState === "ending";
          this.setState({ callState: "disconnected", isConnected: false });
          // Ensure hardware camera/mic tracks are released and callbacks fired
          if (!wasEnding) {
            this.hangUp().then(() => {
              this.onCallEndedCallbacks.forEach(cb => cb());
            });
          }
        }
      };
      call.on("stateChanged", this.stateChangedHandler);

      this.remoteParticipantsUpdatedHandler = (e: any) => {
        e.added.forEach((p: any) => {
          if (this.remoteLeftTimeout) {
            clearTimeout(this.remoteLeftTimeout);
            this.remoteLeftTimeout = null;
            console.log("[CallManager] Remote participant rejoined, cancelled disconnect timeout.");
          }
          this.handleParticipant(p);
        });
        e.removed.forEach((p: any) => {
          const participantId = (p.identifier as any)?.communicationUserId || String(p.identifier);
          this.subscribedParticipants.delete(participantId);
        });
        if (e.removed.length > 0 && call.remoteParticipants.length === 0) {
          // Patient hung up / left the call — wait 15 seconds grace period in case of a glitch
          if (this.remoteLeftTimeout) {
            clearTimeout(this.remoteLeftTimeout);
          }
          console.log("[CallManager] Remote participant disconnected. Starting 15s grace period before ending call.");
          this.remoteLeftTimeout = setTimeout(() => {
            console.log("[CallManager] Grace period expired. Hanging up call.");
            this.hangUp().then(() => {
              this.onCallEndedCallbacks.forEach((cb) => cb());
            });
          }, 15000);
        }
      };
      call.on("remoteParticipantsUpdated", this.remoteParticipantsUpdatedHandler);
      
      call.remoteParticipants.forEach((p) => this.handleParticipant(p));
    } catch (err) {
      console.error("[CallManager] joinRoom error:", err);
      this.setState({ callState: "idle" });
    }
  }

  /**
   * Explicitly hang up and release all resources.
   * Only called by the doctor pressing the end call button or on call disconnect.
   */
  async hangUp() {
    this.setState({ callState: "ending" });
    this.subscribedParticipants.clear();
    this.isRenderingRemote = false;

    if (this.remoteLeftTimeout) {
      clearTimeout(this.remoteLeftTimeout);
      this.remoteLeftTimeout = null;
    }

    if (this.call && this.localVideoStream) {
      try {
        await this.call.stopVideo(this.localVideoStream);
      } catch (_) {}
    }

    // 1. Stop the local video stream source tracks directly (camera hardware)
    if (this.localVideoStream) {
      try {
        const source = (this.localVideoStream as any).source;
        if (source && source.getMediaStream) {
          const ms = source.getMediaStream();
          if (ms) ms.getTracks().forEach((t: MediaStreamTrack) => t.stop());
        }
      } catch (_) {}
    }

    // 2. Stop hardware media tracks on video/audio elements inside our target views
    try {
      const targets = [this.localVideoTarget, this.remoteVideoTarget].filter(Boolean);
      targets.forEach((target) => {
        target!.querySelectorAll("video, audio").forEach((el: any) => {
          const src = el.srcObject as MediaStream | null;
          if (src) {
            src.getTracks().forEach((t: MediaStreamTrack) => t.stop());
            el.srcObject = null;
          }
        });
      });
    } catch (_) {}

    // 3. Dispose renderers
    if (this.localRenderer) {
      try { this.localRenderer.dispose(); } catch (_) {}
      this.localRenderer = null;
    }
    if (this.remoteRenderer) {
      try { this.remoteRenderer.dispose(); } catch (_) {}
      this.remoteRenderer = null;
    }

    // 4. Clear DOM targets
    if (this.localVideoTarget) this.localVideoTarget.innerHTML = "";
    if (this.remoteVideoTarget) this.remoteVideoTarget.innerHTML = "";
    this.localVideoTarget = null;
    this.remoteVideoTarget = null;

    // 5. Hang up the call
    if (this.call) {
      if (this.stateChangedHandler) {
        try { this.call.off("stateChanged", this.stateChangedHandler); } catch (_) {}
        this.stateChangedHandler = null;
      }
      if (this.remoteParticipantsUpdatedHandler) {
        try { this.call.off("remoteParticipantsUpdated", this.remoteParticipantsUpdatedHandler); } catch (_) {}
        this.remoteParticipantsUpdatedHandler = null;
      }
      try { await this.call.hangUp(); } catch (_) {}
      this.call = null;
    }

    // 6. Dispose call agent (releases ACS internal media resources)
    if (this.callAgent) {
      try { await this.callAgent.dispose(); } catch (_) {}
      this.callAgent = null;
    }

    this.localVideoStream = null;
    this.deviceManager = null;

    // 7. Clean up all participant and stream event listeners
    this.eventCleanups.forEach((cleanup) => cleanup());
    this.eventCleanups = [];

    // 8. Stop all tracked media streams (foolproof capture release)
    try {
      activeStreams.forEach((stream) => {
        try {
          stream.getTracks().forEach((t) => {
            try {
              t.stop();
            } catch (_) {}
          });
        } catch (_) {}
      });
      activeStreams.clear();
    } catch (_) {}

    this.setState({
      callState: "idle",
      isConnected: false,
      isMicOn: true,
      isCameraOn: true,
      remoteParticipantName: "",
      hasRemoteVideo: false,
    });
  }

  async toggleMic() {
    if (!this.call) return;
    const next = !this.state.isMicOn;
    try {
      if (this.state.isMicOn) await this.call.mute();
      else await this.call.unmute();
      this.setState({ isMicOn: next });
    } catch (err) {
      console.error("[CallManager] toggleMic:", err);
    }
  }

  async toggleCamera() {
    if (!this.call) return;
    const next = !this.state.isCameraOn;
    try {
      if (this.state.isCameraOn) {
        if (this.localVideoStream) {
          await this.call.stopVideo(this.localVideoStream);
        }
        // Clear local preview
        if (this.localVideoTarget) this.localVideoTarget.innerHTML = "";
        if (this.localRenderer) {
          try { this.localRenderer.dispose(); } catch (_) {}
          this.localRenderer = null;
        }
      } else {
        // Create a fresh LocalVideoStream — can't restart a stopped one
        if (this.deviceManager) {
          const cameras = await this.deviceManager.getCameras();
          if (cameras.length > 0) {
            const newStream = new LocalVideoStream(cameras[0]);
            this.localVideoStream = newStream;
            await this.call.startVideo(newStream);
            if (this.localVideoTarget) {
              await this.renderLocalStream();
            }
          }
        }
      }
      this.setState({ isCameraOn: next });
    } catch (err) {
      console.error("[CallManager] toggleCamera:", err);
    }
  }

  async switchCamera() {
    if (!this.deviceManager || !this.localVideoStream) return;
    try {
      const cameras = await this.deviceManager.getCameras();
      if (cameras.length <= 1) return; // No other camera to switch to
      const currentSource = this.localVideoStream.source;
      const currentIndex = cameras.findIndex(c => c.id === currentSource.id);
      const nextIndex = (currentIndex + 1) % cameras.length;
      const nextCamera = cameras[nextIndex];
      await this.localVideoStream.switchSource(nextCamera);
    } catch (err) {
      console.error("[CallManager] switchCamera failed:", err);
    }
  }


  // ─── Private helpers ─────────────────────────────────────

  private setState(partial: Partial<CallManagerState>) {
    this.state = { ...this.state, ...partial };
    this.listeners.forEach((l) => l({ ...this.state }));
  }

  private async renderLocalStream() {
    if (!this.localVideoStream || !this.localVideoTarget) return;
    if (this.localRenderer) {
      try { this.localRenderer.dispose(); } catch (_) {}
      this.localRenderer = null;
    }
    try {
      const renderer = new VideoStreamRenderer(this.localVideoStream);
      this.localRenderer = renderer;
      const view = await renderer.createView({ scalingMode: "Crop" });
      if (this.localVideoTarget) {
        this.localVideoTarget.innerHTML = "";
        this.localVideoTarget.appendChild(view.target);
      }
    } catch (err) {
      console.error("[CallManager] renderLocalStream:", err);
    }
  }

  private async renderRemoteStream(stream: RemoteVideoStream) {
    if (this.isRenderingRemote) return; // prevent concurrent renders
    this.isRenderingRemote = true;
    try {
      if (this.remoteRenderer) {
        try { this.remoteRenderer.dispose(); } catch (_) {}
        this.remoteRenderer = null;
      }
      const renderer = new VideoStreamRenderer(stream);
      this.remoteRenderer = renderer;
      const view = await renderer.createView({ scalingMode: "Crop" });
      if (this.remoteVideoTarget) {
        this.remoteVideoTarget.innerHTML = "";
        this.remoteVideoTarget.appendChild(view.target);

        this.setState({ hasRemoteVideo: true });
      }
    } catch (err: any) {
      console.warn("[CallManager] renderRemoteStream:", err?.message);
    } finally {
      this.isRenderingRemote = false;
    }
  }

  private clearRemoteVideo() {
    if (this.remoteRenderer) {
      try { this.remoteRenderer.dispose(); } catch (_) {}
      this.remoteRenderer = null;
    }
    if (this.remoteVideoTarget) this.remoteVideoTarget.innerHTML = "";
    this.setState({ hasRemoteVideo: false });
  }

  private handleParticipant(participant: any) {
    const participantId =
      participant.identifier?.communicationUserId ||
      String(participant.identifier);
    if (this.subscribedParticipants.has(participantId)) return;
    this.subscribedParticipants.add(participantId);

    this.setState({
      remoteParticipantName: (participant as any).displayName || "Patient",
    });

    const subscribeToStream = (stream: RemoteVideoStream) => {
      const onAvailableChanged = () => {
        if (stream.isAvailable) this.renderRemoteStream(stream);
        else this.clearRemoteVideo();
      };
      stream.on("isAvailableChanged", onAvailableChanged);
      this.eventCleanups.push(() => {
        try { stream.off("isAvailableChanged", onAvailableChanged); } catch (_) {}
      });

      if (stream.isAvailable) this.renderRemoteStream(stream);
    };

    const onVideoStreamsUpdated = (e: any) => {
      e.added.forEach(subscribeToStream);
    };
    participant.on("videoStreamsUpdated", onVideoStreamsUpdated);
    this.eventCleanups.push(() => {
      try { participant.off("videoStreamsUpdated", onVideoStreamsUpdated); } catch (_) {}
    });

    participant.videoStreams.forEach(subscribeToStream);
  }
}

// Singleton — one instance for the entire app session
export const callManager = new CallManagerClass();
